import React from 'react';
import { MarketingPage, MarketingContent } from '../components/MarketingPage';

const content: MarketingContent = {
  seo: {
    title: 'AirDrop for Windows — Send Files Between iPhone, Android & PC',
    description:
      'Windows has no AirDrop. Local Area Cloud gives you the same instant, wireless transfer — and it works across Windows, Mac, Linux, iPhone, and Android. Free for 2 devices.',
    path: '/airdrop-for-windows',
  },
  eyebrow: 'AirDrop alternative',
  h1: <>AirDrop for Windows — <span className="em">the one Windows never shipped.</span></>,
  lead:
    'Apple’s AirDrop stops at Apple devices, and Windows has no built-in equivalent that reaches your phone. Local Area Cloud gives you that instant, wireless, phone-to-PC transfer — and it works across Windows, macOS, Linux, iPhone, and Android.',
  sections: [
    {
      h2: 'Why there’s no real AirDrop on Windows',
      paras: [
        'AirDrop is Apple-only — it will never move a file from your iPhone to a Windows PC. Windows’ own “Nearby Sharing” only talks to other Windows machines, and Google’s Quick Share is built for Android. So the most common transfer people actually need — iPhone or Android to a Windows desktop — falls through the cracks.',
        'Most people end up emailing files to themselves, uploading to a cloud drive and downloading again, or digging out a cable. Local Area Cloud replaces all of that with a direct, over-the-network send.',
      ],
    },
    {
      h2: 'How Local Area Cloud works like AirDrop — everywhere',
      paras: [
        'Install the app on both devices and sign in to the same free account. Your devices discover each other on your local network, you pick one, and the file goes straight across at full LAN speed. The file data never touches our servers — our cloud only helps your own devices find each other.',
      ],
      bullets: [
        <><b>Cross-platform:</b> iPhone ⇄ Windows, Android ⇄ Mac, PC ⇄ PC — any direction.</>,
        <><b>Local-network speed:</b> no upload-then-download round trip through the internet.</>,
        <><b>Private by design:</b> your files are never stored, scanned, or routed by us.</>,
        <><b>No size ceiling:</b> send large videos and folders, not just what fits in an email.</>,
      ],
    },
    {
      h2: 'AirDrop vs. Local Area Cloud',
      table: {
        head: ['', 'AirDrop', 'Local Area Cloud'],
        rows: [
          ['Works on Windows', { v: 'No', kind: 'no' }, { v: 'Yes', kind: 'yes' }],
          ['iPhone ⇄ Windows', { v: 'No', kind: 'no' }, { v: 'Yes', kind: 'yes' }],
          ['Android support', { v: 'No', kind: 'no' }, { v: 'Yes', kind: 'yes' }],
          ['Files stay off the cloud', { v: 'Yes', kind: 'yes' }, { v: 'Yes', kind: 'yes' }],
          ['Automatic backups', { v: 'No', kind: 'no' }, { v: 'Yes', kind: 'yes' }],
          ['Price', 'Free (Apple only)', 'Free for 2 devices'],
        ],
      },
    },
    {
      h2: 'Set it up in three steps',
      bullets: [
        <><b>1. Download</b> Local Area Cloud on your Windows PC and your phone.</>,
        <><b>2. Sign in</b> to the same free account on both (email, Google, or Microsoft).</>,
        <><b>3. Send</b> — pick the other device, choose your files, and they arrive in seconds.</>,
      ],
    },
  ],
  faqs: [
    {
      q: 'Is Local Area Cloud free?',
      a: 'Yes. The free plan covers up to two devices with every feature. Pro adds unlimited devices and secure remote access over your own VPN, sold only on the website.',
    },
    {
      q: 'Does it really work between an iPhone and a Windows PC?',
      a: 'Yes — that’s the main gap it fills. Install the app on both, sign in to the same account, and you can send files in either direction.',
    },
    {
      q: 'Do my files go through your servers?',
      a: 'No. Files transfer directly between your devices over your local network. Our servers only act as a registry so your devices can find each other; they never see your file contents.',
    },
    {
      q: 'Do both devices need to be on the same Wi-Fi network?',
      a: 'For the free local transfer, yes — both devices should be on the same network. Pro adds remote reach across networks using your own VPN.',
    },
    {
      q: 'Is there a file-size limit?',
      a: 'No fixed cap like email attachments. You can send large videos and whole folders between your devices.',
    },
  ],
  related: [
    { label: 'Transfer files between phone and PC', to: '/transfer-files-phone-to-pc', note: 'The phone ⇄ computer job, explained' },
    { label: 'Local network file sharing', to: '/local-network-file-sharing', note: 'How LAN transfer works' },
    { label: 'File sharing without the cloud', to: '/file-sharing-without-the-cloud', note: 'The privacy angle' },
  ],
  ctaBand: {
    h2: 'The AirDrop Windows should have had.',
    p: 'Install on your PC and phone, sign in once, and send. Free for up to two devices.',
  },
};

export const AirdropForWindows: React.FC = () => <MarketingPage content={content} />;
export default AirdropForWindows;
